import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Search, Users, User, ChevronLeft, ChevronRight, Loader, Compass, Globe, MapPin, GraduationCap, Crown, Lock, DoorOpen, SlidersHorizontal } from 'lucide-react';
import 'mapbox-gl/dist/mapbox-gl.css';
import StickmanRunning from './StickmanRunning';
import api, { getCached } from '../config/api';
import HobbiesDropdown from './HobbiesDropdown';
import SmartTagsInput from './SmartTagsInput';
import { detectUserLocation } from '../utils/locationUtils';
import { universityLocations } from '../data/universityLocations';
import { countries, usStates } from '../data/usStates';
import { hobbies } from '../data/hobbies';
import { getFirstOrderTags } from '../data/tagHierarchy';
import OrchestratorAgent from '../agents/OrchestratorAgent';
import WorldMapCopy from './WorldMapCopy';

import GeminiLogo from './GeminiLogo';

const geocodeLocation = async (query, mapboxToken) => {
  if (!query || !mapboxToken) return null;
  try {
    const res = await fetch(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=${mapboxToken}&limit=1&types=place,address`
    );
    if (!res.ok) return null;
    const data = await res.json();
    if (data.features?.[0]?.center) {
      const [lng, lat] = data.features[0].center;
      return [lng, lat];
    }
    return null;
  } catch {
    return null;
  }
};

// Scroll-reveal hook: observes elements and adds 'revealed' class when visible
const useScrollReveal = () => {
  const refs = useRef([]);

  const addRef = useCallback((el) => {
    if (el && !refs.current.includes(el)) refs.current.push(el);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }
    );

    refs.current.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return addRef;
};

// School Logo Component - Tries multiple image sources
const SchoolLogo = ({ name, localLogo, logoUrl, fallbackText, color, websiteUrl }) => {
  const [currentSrc, setCurrentSrc] = useState(null);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgError, setImgError] = useState(false);

  const colorStyles = {
    'purple-700': 'text-purple-700 dark:text-purple-300 bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800/40',
    'red-700': 'text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800/40',
    'gray-800': 'text-gray-800 dark:text-gray-300 bg-gray-50 dark:bg-[#1f1f1f] border-gray-200 dark:border-[#333]',
    'blue-700': 'text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800/40',
    'green-700': 'text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800/40',
    'blue-600': 'text-blue-600 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800/40',
    'orange-600': 'text-orange-600 dark:text-orange-300 bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800/40'
  };

  const classes = colorStyles[color] || 'text-gray-800 dark:text-gray-300 bg-gray-50 dark:bg-[#1f1f1f] border-gray-200 dark:border-[#333]';
  const isTwoLine = fallbackText.includes(' ');

  // Try to load images in order: local first, then online
  useEffect(() => {
    const tryLoadImage = (src) => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(src);
        img.onerror = () => reject();
        img.src = src;
      });
    };

    const loadImage = async () => {
      // Try local first
      if (localLogo) {
        try {
          await tryLoadImage(localLogo);
          setCurrentSrc(localLogo);
          setImgLoaded(true);
          return;
        } catch (e) {
          // Local failed, try online
        }
      }
      
      // Try online URL
      if (logoUrl) {
        try {
          await tryLoadImage(logoUrl);
          setCurrentSrc(logoUrl);
          setImgLoaded(true);
        } catch (e) {
          // Both failed
          setImgError(true);
        }
      } else {
        setImgError(true);
      }
    };

    loadImage();
  }, [localLogo, logoUrl]);

  const content = (
    <div className={`w-full h-24 flex items-center justify-center ${imgError ? classes : ''}`}>
      {imgLoaded && currentSrc && !imgError ? (
        <img 
          src={currentSrc}
          alt={name}
          className="h-[67px] w-auto object-contain max-w-full"
          style={{ 
            maxHeight: '67px',
            width: 'auto'
          }}
        />
      ) : (
        <div className={`font-bold text-center ${isTwoLine ? 'text-sm leading-tight' : 'text-base'}`}>
          {fallbackText.split(' ').map((word, i, arr) => (
            <React.Fragment key={i}>
              {word}
              {i < arr.length - 1 && <br />}
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );

  if (websiteUrl) {
    return (
      <a 
        href={websiteUrl} 
        target="_blank" 
        rel="noopener noreferrer"
        className="block hover:opacity-80 transition-opacity"
        aria-label={`Visit ${name} website`}
      >
        {content}
      </a>
    );
  }

  return content;
};

const Home = ({ user }) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [selectedResultIndex, setSelectedResultIndex] = useState(-1);
  const [isSearching, setIsSearching] = useState(false);
  const searchInputRef = useRef(null);
  const searchResultsRef = useRef(null);
  const reveal = useScrollReveal();
  
  useEffect(() => {
    const storedSessionId = localStorage.getItem('sessionId');
    if (storedSessionId) {
      setSessionId(storedSessionId);
    }
  }, []);
  const [selectedHobbies, setSelectedHobbies] = useState([]);
  const [clubSize, setClubSize] = useState('');
  const [clubType, setClubType] = useState('');
  const [timing, setTiming] = useState(''); // '', 'daytime', 'noon', 'evening', 'night'
  const [isTimingOpen, setIsTimingOpen] = useState(false);
  const [tags, setTags] = useState([]);
  const [selectedSchool, setSelectedSchool] = useState(() => {
    return user?.school || sessionStorage.getItem('selectedSchool') || '';
  });
  const [schools, setSchools] = useState([]);
  const [state, setState] = useState('');
  const [city, setCity] = useState('');
  const [area, setArea] = useState('');
  const [pincode, setPincode] = useState('');
  const [country, setCountry] = useState('US');
  const [showCountryPicker, setShowCountryPicker] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [locationPermissionAsked, setLocationPermissionAsked] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [isDetectingLocation, setIsDetectingLocation] = useState(false);
  const [locationError, setLocationError] = useState('');
  const [clubsNearYouCount, setClubsNearYouCount] = useState(null);
  const [isLoadingClubsCount, setIsLoadingClubsCount] = useState(false);
  const [mapCenter, setMapCenter] = useState(null);
  const locationMapRef = useRef(null);
  const locationMapInstanceRef = useRef(null);
  const locationPinOverlayRef = useRef(null);
  const locationPinElementRef = useRef(null);
  const locationPinCleanupRef = useRef(null);
  
  // Mapbox token from environment variable
  const mapboxToken = process.env.REACT_APP_MAPBOX_TOKEN || 'pk.eyJ1Ijoic3Rhcm9pcjExIiwiYSI6ImNta3g0c2NqdTA2Yncza29ncm55ZjU4anQifQ.8vEy7h6GCWuZAvmyqZAAsw';
  
  // Multi-Agent System
  const orchestratorRef = useRef(new OrchestratorAgent());
  const [isAgentThinking, setIsAgentThinking] = useState(false);
  const [agentSuggestions, setAgentSuggestions] = useState(null);

  const activities = [
    {
      id: 1,
      title: 'Running',
      description: 'Join Running & Fitness groups',
      image: '/novemb.jpg'
    },
    {
      id: 2,
      title: 'Tennis',
      description: 'Play & Compete',
      image: '/tennis.jpg'
    },
    {
      id: 3,
      title: 'Reading',
      description: 'Book clubs & discussions',
      image: '/reading.jpg'
    },
    {
      id: 4,
      title: 'Hiking',
      description: 'Explore nature together',
      image: '/hike3.jpg'
    },
    {
      id: 5,
      title: 'Dinner',
      description: 'Food & Fellowship',
      image: '/kings.jpg'
    },
    {
      id: 6,
      title: 'Spiritual',
      description: 'Faith & Fellowship',
      image: '/chruch2.jpg'
    }
  ];

  // Maximum slide index (since we show 2 at a time, max is length - 2)
  const maxSlide = activities.length - 2;

  // Carousel stays static on load (no auto-play); user can still use arrows/dots to change slides

  // Initialize Multi-Agent System
  useEffect(() => {
    // Get user profile from localStorage if available
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const userProfile = JSON.parse(storedUser);
        orchestratorRef.current.initialize(userProfile);
      } catch (e) {
        console.error('Error parsing user profile:', e);
      }
    }
  }, []);

  // Request location permission on page load and fetch schools (cached 5 min)
  useEffect(() => {
    getCached('/schools', { ttlMs: 5 * 60 * 1000 })
      .then(res => setSchools(res.data || []))
      .catch(() => {});
    
    // Use Multi-Agent System for intelligent location detection
    const hasAsked = sessionStorage.getItem('locationPermissionAsked');
    
    if (!hasAsked) {
      setIsAgentThinking(true);
      const timer = setTimeout(async () => {
        try {
          // Use Orchestrator Agent for intelligent location detection
          const intelligentLocation = await orchestratorRef.current.getIntelligentLocation({
            useGPS: true,
            university: null,
            universityLocations
          });

          if (intelligentLocation) {
            setState(intelligentLocation.state);
            setCity(intelligentLocation.city);
            if (intelligentLocation.area) setArea(intelligentLocation.area);
            if (intelligentLocation.pincode) setPincode(intelligentLocation.pincode);
            
            // Get intelligent hobby suggestions
            const intelligentHobbies = await orchestratorRef.current.getIntelligentHobbies({
              includeProfile: true,
              includeMajors: true,
              includeHistory: false,
              includePopular: true
            });
            
            if (intelligentHobbies.length > 0) {
              const validHobbies = intelligentHobbies.filter((h) => hobbies.includes(h));
              setSelectedHobbies(validHobbies.slice(0, 3)); // Auto-select up to 3 from dropdown list only
            }

            // Get intelligent tag suggestions
            const intelligentTags = orchestratorRef.current.getIntelligentTags({
              hobbies: intelligentHobbies
            });
            
            setAgentSuggestions({
              location: intelligentLocation,
              hobbies: intelligentHobbies,
              tags: intelligentTags
            });
          }
        } catch (error) {
          console.log('Agent-based location detection skipped:', error.message);
        } finally {
          setIsAgentThinking(false);
          setLocationPermissionAsked(true);
          sessionStorage.setItem('locationPermissionAsked', 'true');
        }
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, []);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % (maxSlide + 1));
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + maxSlide + 1) % (maxSlide + 1));
  };

  // Real-time search for groups
  useEffect(() => {
    const searchGroups = async () => {
      if (!searchQuery.trim()) {
        setSearchResults([]);
        setShowSearchResults(false);
        return;
      }

      setIsSearching(true);
      try {
        // Search for groups across all fields (name, description, tags, hobbies, location, etc.)
        const response = await api.get(`/groups?search=${encodeURIComponent(searchQuery)}`);
        const groups = Array.isArray(response.data) ? response.data : [];
        
        
        const filtered = groups.slice(0, 10);
        
        setSearchResults(filtered);
        setShowSearchResults(true);
        setSelectedResultIndex(-1);
      } catch (error) {
        console.error('Error searching groups:', error);
        console.error('Error details:', error.response?.data || error.message);
        setSearchResults([]);
        setShowSearchResults(true);
      } finally {
        setIsSearching(false);
      }
    };

    const debounceTimer = setTimeout(searchGroups, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchQuery]);

  // Keyboard navigation for search results
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!showSearchResults || searchResults.length === 0) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedResultIndex(prev => 
          prev < searchResults.length - 1 ? prev + 1 : prev
        );
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedResultIndex(prev => prev > 0 ? prev - 1 : -1);
      } else if (e.key === 'Enter' && selectedResultIndex >= 0) {
        e.preventDefault();
        const selectedGroup = searchResults[selectedResultIndex];
        if (selectedGroup) {
          navigate(`/groups/${selectedGroup.id}`);
          setShowSearchResults(false);
        }
      } else if (e.key === 'Escape') {
        setShowSearchResults(false);
        setSearchQuery('');
        searchInputRef.current?.blur();
      }
    };

    if (showSearchResults) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [showSearchResults, searchResults, selectedResultIndex]);

  // Scroll selected result into view
  useEffect(() => {
    if (selectedResultIndex >= 0 && searchResultsRef.current) {
      const selectedElement = searchResultsRef.current.children[selectedResultIndex];
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [selectedResultIndex]);

  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target) &&
        searchResultsRef.current &&
        !searchResultsRef.current.contains(event.target)
      ) {
        setShowSearchResults(false);
      }
    };

    if (showSearchResults) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showSearchResults]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      if (selectedResultIndex >= 0 && searchResults[selectedResultIndex]) {
        navigate(`/groups/${searchResults[selectedResultIndex].id}`);
        setShowSearchResults(false);
      } else if (searchResults.length > 0) {
        navigate(`/groups/${searchResults[0].id}`);
        setShowSearchResults(false);
      } else {
        navigate(`/groups?search=${encodeURIComponent(searchQuery)}`);
        setShowSearchResults(false);
      }
    }
  };

  const handleResultClick = (groupId) => {
    navigate(`/groups/${groupId}`);
    setShowSearchResults(false);
  };

  const handleUniversityChange = async (schoolName) => {
    setSelectedSchool(schoolName);
    if (schoolName) sessionStorage.setItem('selectedSchool', schoolName);
    else sessionStorage.removeItem('selectedSchool');
    
    // Use Multi-Agent System for intelligent location inference
    if (schoolName) {
      setIsAgentThinking(true);
      try {
        const intelligentLocation = await orchestratorRef.current.getIntelligentLocation({
          useGPS: false,
          university: schoolName,
          universityLocations
        });

        if (intelligentLocation) {
          setState(intelligentLocation.state);
          setCity(intelligentLocation.city);
          setArea('');
          setPincode('');
          
          // Get intelligent hobby suggestions based on university context
          const intelligentHobbies = await orchestratorRef.current.getIntelligentHobbies({
            includeProfile: true,
            includeMajors: true,
            includePopular: true
          });
          
          if (intelligentHobbies.length > 0) {
            setSelectedHobbies(intelligentHobbies.slice(0, 3));
          }
        } else if (universityLocations[schoolName]) {
          // Fallback to manual mapping
          const location = universityLocations[schoolName];
          setState(location.state);
          setCity(location.city);
          setArea('');
          setPincode('');
        }
      } catch (error) {
        console.error('Error in agent-based university location:', error);
        // Fallback to manual mapping
        if (universityLocations[schoolName]) {
          const location = universityLocations[schoolName];
          setState(location.state);
          setCity(location.city);
          setArea('');
          setPincode('');
        }
      } finally {
        setIsAgentThinking(false);
      }
    } else {
      // If "All Universities" is selected, clear location
      setState('');
      setCity('');
      setArea('');
      setPincode('');
      setMapCenter(null);
    }
  };

  const handleUseMyLocation = async () => {
    setIsDetectingLocation(true);
    setLocationError('');
    try {
      const data = await detectUserLocation();
      setState(data.state);
      setCity(data.city);
      if (data.area) setArea(data.area);
      if (data.pincode) setPincode(data.pincode);
    } catch (err) {
      console.error('Location detection error:', err);
      if (err?.code === 1) {
        setLocationError('Location access denied. Please enable location in your browser.');
      } else if (err?.code === 2) {
        setLocationError('Location unavailable. Please check your device settings.');
      } else {
        setLocationError(err?.message || 'Unable to detect location. Please try again.');
      }
    } finally {
      setIsDetectingLocation(false);
    }
  };

  // Geocode city+state for map center
  useEffect(() => {
    if (!state || !city || !mapboxToken) {
      setMapCenter(null);
      return;
    }
    const stateName = usStates.find(s => s.code === state)?.name || state;
    const query = `${city}, ${stateName}`;
    let cancelled = false;
    geocodeLocation(query, mapboxToken).then((coords) => {
      if (!cancelled && coords) setMapCenter(coords);
    });
    return () => { cancelled = true; };
  }, [state, city, mapboxToken]);

  // Fetch clubs count when location is set
  useEffect(() => {
    if (!state || !city) {
      setClubsNearYouCount(null);
      return;
    }
    let cancelled = false;
    setIsLoadingClubsCount(true);
    api.post('/recommendations', { city: city.trim(), state })
      .then((res) => {
        if (!cancelled && Array.isArray(res.data)) {
          setClubsNearYouCount(res.data.length);
        }
      })
      .catch(() => {
        if (!cancelled) setClubsNearYouCount(null);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingClubsCount(false);
      });
    return () => { cancelled = true; };
  }, [state, city]);

  const DEFAULT_MAP_CENTER = [-122.3321, 47.6062]; // Seattle

  // Small map: create once when container and token are ready
  useEffect(() => {
    if (!locationMapRef.current || !mapboxToken) return;
    import('mapbox-gl').then((mapboxgl) => {
      mapboxgl.default.accessToken = mapboxToken;
      const map = new mapboxgl.default.Map({
        container: locationMapRef.current,
        style: 'mapbox://styles/mapbox/streets-v12',
        center: DEFAULT_MAP_CENTER,
        zoom: 10,
        attributionControl: false
      });
      locationMapInstanceRef.current = map;
    });
    return () => {
      if (locationPinCleanupRef.current) {
        locationPinCleanupRef.current();
        locationPinCleanupRef.current = null;
      }
      if (locationPinElementRef.current?.parentNode) {
        locationPinElementRef.current.parentNode.removeChild(locationPinElementRef.current);
        locationPinElementRef.current = null;
      }
      if (locationMapInstanceRef.current) {
        locationMapInstanceRef.current.remove();
        locationMapInstanceRef.current = null;
      }
    };
  }, [mapboxToken]);

  // Update map center and marker when location is set (pin in overlay so it stays blue, map stays grey)
  useEffect(() => {
    const map = locationMapInstanceRef.current;
    const overlay = locationPinOverlayRef.current;
    if (!map) return;
    if (locationPinCleanupRef.current) {
      locationPinCleanupRef.current();
      locationPinCleanupRef.current = null;
    }
    if (locationPinElementRef.current && locationPinElementRef.current.parentNode) {
      locationPinElementRef.current.parentNode.removeChild(locationPinElementRef.current);
      locationPinElementRef.current = null;
    }
    if (mapCenter && overlay) {
      const [lng, lat] = mapCenter;
      map.flyTo({ center: [lng, lat], zoom: 12, duration: 800 });
      const updatePinPosition = () => {
        const pt = map.project([lng, lat]);
        if (locationPinElementRef.current) {
          locationPinElementRef.current.style.left = `${pt.x - 12}px`;
          locationPinElementRef.current.style.top = `${pt.y - 12}px`;
        }
      };
      import('mapbox-gl').then(() => {
        const userEl = document.createElement('div');
        locationPinElementRef.current = userEl;
        userEl.className = 'user-location-marker';
        userEl.style.position = 'absolute';
        userEl.style.width = '24px';
        userEl.style.height = '24px';
        userEl.style.borderRadius = '50%';
        userEl.style.backgroundColor = '#2563eb';
        userEl.style.border = '4px solid white';
        userEl.style.boxShadow = '0 0 0 0 rgba(37, 99, 235, 0.5), 0 2px 12px rgba(37, 99, 235, 0.4)';
        userEl.style.display = 'block';
        userEl.style.zIndex = '1000';
        userEl.style.pointerEvents = 'none';
        const pulse = document.createElement('div');
        pulse.style.position = 'absolute';
        pulse.style.top = '50%';
        pulse.style.left = '50%';
        pulse.style.transform = 'translate(-50%, -50%)';
        pulse.style.width = '24px';
        pulse.style.height = '24px';
        pulse.style.borderRadius = '50%';
        pulse.style.backgroundColor = 'rgba(37, 99, 235, 0.35)';
        pulse.style.animation = 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite';
        userEl.appendChild(pulse);
        if (!document.getElementById('map-pulse-animation')) {
          const style = document.createElement('style');
          style.id = 'map-pulse-animation';
          style.textContent = `
            @keyframes pulse {
              0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 1; }
              50% { transform: translate(-50%, -50%) scale(2); opacity: 0; }
            }
          `;
          document.head.appendChild(style);
        }
        overlay.appendChild(userEl);
        updatePinPosition();
        map.on('move', updatePinPosition);
        map.on('zoom', updatePinPosition);
        locationPinCleanupRef.current = () => {
          map.off('move', updatePinPosition);
          map.off('zoom', updatePinPosition);
        };
      });
    } else {
      map.flyTo({ center: DEFAULT_MAP_CENTER, zoom: 10, duration: 600 });
    }
  }, [mapCenter]);

  // Handle intelligent button
  const handleIntelligentFill = async () => {
    setIsAgentThinking(true);
    try {
      const recommendations = await orchestratorRef.current.getCompleteRecommendations({
        useGPS: true,
        university: selectedSchool,
        universityLocations
      });

      if (recommendations.location) {
        setState(recommendations.location.state);
        setCity(recommendations.location.city);
        if (recommendations.location.area) setArea(recommendations.location.area);
        if (recommendations.location.pincode) setPincode(recommendations.location.pincode);
      }

      // Pick exactly 3 categories from the Hobbies dropdown list
      const categories = hobbies.filter((c) => c !== 'All Clubs');
      const shuffled = [...categories].sort(() => Math.random() - 0.5);
      const chosen = shuffled.slice(0, 3);
      setSelectedHobbies(chosen);

      // Additional tags: one tag per category, in order (1st from 1st category, 2nd from 2nd, 3rd from 3rd)
      const additionalTags = chosen
        .map((cat) => {
          const options = getFirstOrderTags(cat);
          return options.length > 0 ? options[0] : null;
        })
        .filter(Boolean);
      setTags(additionalTags);

      setAgentSuggestions(recommendations);
    } catch (error) {
      console.error('Error in intelligent fill:', error);
      alert('Unable to auto-fill. Please select manually.');
    } finally {
      setIsAgentThinking(false);
    }
  };

  const handleGetRecommendations = async () => {
    // Allow recommendations if university is selected OR location is provided
    if (!selectedSchool && (!state || !city)) {
      alert('Please select a university or enter your location to get recommendations!');
      return;
    }

    // Tags are optional - can search with just location, or location + hobbies
    setIsLoading(true);
    try {
      const locationData = {
        city: city.trim() || undefined,
        state: state || undefined,
        area: area.trim() || undefined,
        pincode: pincode.trim() || undefined
      };

      const response = await api.post('/recommendations', {
        interests: selectedHobbies,
        tags: tags,
        school: selectedSchool || undefined,
        clubSize: clubSize || undefined,
        clubType: clubType || undefined,
        timing: timing || undefined,
        ...locationData
      });

      // Store recommendations and location data
      sessionStorage.setItem('recommendations', JSON.stringify(response.data));
      sessionStorage.setItem('recommendationLocation', JSON.stringify({
        state,
        city,
        area,
        pincode,
        hobbies: selectedHobbies,
        tags,
        school: selectedSchool,
        clubSize: clubSize || undefined,
        clubType: clubType || undefined,
        timing: timing || undefined
      }));
      window.location.href = '/recommendations';
    } catch (error) {
      console.error('Error getting recommendations:', error);
      if (error.response?.data?.error) {
        alert(error.response.data.error);
      } else {
        alert('Failed to get recommendations. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f5f5f7] dark:bg-[#0f0f0f]">
      {/* Image Carousel Section */}
      <div className="relative w-full h-[60vh] overflow-hidden bg-[#f5f5f7] dark:bg-[#0f0f0f] px-6 py-5">
        <div 
          className="flex h-full gap-5"
          style={{ transform: `translateX(-${currentSlide * 50}%)` }}
        >
          {activities.map((activity, index) => (
            <div
              key={activity.id}
              className="relative w-1/2 flex-shrink-0 h-full overflow-hidden rounded-[1.75rem]"
            >
                  <img
                    src={activity.image}
                    alt={activity.title}
                    className="w-full h-full object-cover"
                    style={activity.title === 'Spiritual' ? { objectPosition: '35% center' } : undefined}
                    loading="eager"
                    onError={(e) => {
                      // Multiple fallback URLs for tennis
                      if (activity.title === 'Tennis') {
                        const fallbacks = [
                          'https://images.unsplash.com/photo-1622279457486-62dcc4a431f3?w=1200&q=90&fit=crop&auto=format',
                          'https://images.unsplash.com/photo-1534158914592-6b425c8bd2c4?w=1200&q=90&fit=crop&auto=format',
                          'https://images.unsplash.com/photo-1594736797933-d0cbc0c6e5a5?w=1200&q=90&fit=crop&auto=format'
                        ];
                        const currentSrc = e.target.src;
                        const nextFallback = fallbacks.find(fb => fb !== currentSrc);
                        if (nextFallback && !e.target.dataset.triedFallback) {
                          e.target.dataset.triedFallback = 'true';
                          e.target.src = nextFallback;
                          return;
                        }
                      }
                      // Fallback URLs for dinner (people dining together)
                      if (activity.title === 'Dinner') {
                        const fallbacks = [
                          'https://images.unsplash.com/photo-1528607929212-2636ec44253e?w=1200&q=90&fit=crop',
                          'https://images.unsplash.com/photo-1556910096-6f5e72db6803?w=1200&q=90&fit=crop',
                          'https://images.unsplash.com/photo-1559339352-11d035aa65de?w=1200&q=90&fit=crop',
                          'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=1200&q=90&fit=crop',
                          'https://images.unsplash.com/photo-1521017432531-fbd92d768814?w=1200&q=90&fit=crop'
                        ];
                        const currentSrc = e.target.src;
                        const nextFallback = fallbacks.find(fb => fb !== currentSrc);
                        if (nextFallback && !e.target.dataset.triedFallback) {
                          e.target.dataset.triedFallback = 'true';
                          e.target.src = nextFallback;
                          return;
                        }
                      }
                      // Generic fallback
                      e.target.src = `https://via.placeholder.com/1200x600/4a5568/ffffff?text=${activity.title}`;
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent flex items-end">
                    <div className="p-10 text-white w-full">
                      <h2 className="text-5xl font-semibold mb-2 tracking-tight">{activity.title}</h2>
                      <p className="text-lg opacity-90 font-medium">{activity.description}</p>
                    </div>
                  </div>
                </div>
              ))}
        </div>

        {/* Navigation Arrows */}
        <button
          onClick={prevSlide}
          className="absolute left-8 top-1/2 transform -translate-y-1/2 bg-white/95 hover:bg-white dark:bg-[#181818]/95 dark:hover:bg-[#202020] rounded-full p-3.5 shadow-xl transition-all duration-300 z-10 border border-gray-100 dark:border-[#2d2d2d]/60 hover:scale-110 backdrop-blur-sm"
          aria-label="Previous slide"
        >
          <ChevronLeft className="h-6 w-6 text-gray-900 dark:text-[#e5e5e5]" />
        </button>
        <button
          onClick={nextSlide}
          className="absolute right-8 top-1/2 transform -translate-y-1/2 bg-white/95 hover:bg-white dark:bg-[#181818]/95 dark:hover:bg-[#202020] rounded-full p-3.5 shadow-xl transition-all duration-300 z-10 border border-gray-100 dark:border-[#2d2d2d]/60 hover:scale-110 backdrop-blur-sm"
          aria-label="Next slide"
        >
          <ChevronRight className="h-6 w-6 text-gray-900 dark:text-[#e5e5e5]" />
        </button>

        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex gap-2 z-10">
          {activities.slice(0, maxSlide + 1).map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                currentSlide === index
                  ? 'w-8 bg-white'
                  : 'w-1.5 bg-white/40 hover:bg-white/70'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>

      {/* Hero Section */}
      <div className="relative overflow-hidden bg-white dark:bg-[#0f0f0f]">
          {/* Spinning world globe — monochrome, modern */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none" aria-hidden="true">
            <div
              className="animate-globe-spin w-[min(90vmin,720px)] h-[min(90vmin,720px)] opacity-[0.12] dark:opacity-[0.15] rounded-full overflow-hidden border border-gray-200/30 dark:border-gray-600/30"
              style={{ clipPath: 'circle(50% at 50% 50%)' }}
            >
              <img
                src="https://upload.wikimedia.org/wikipedia/commons/8/83/Equirectangular_projection_SW.jpg"
                alt=""
                className="w-full h-full object-cover"
                style={{
                  filter: 'grayscale(1) contrast(1.15) brightness(0.75)',
                }}
              />
            </div>
          </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-24 sm:py-32 md:py-40 overflow-visible z-10">
          <div className="text-center overflow-visible translate-x-[15px]">
            {/* Badge */}
            <div className="inline-flex items-center gap-2.5 px-5 py-2.5 bg-gray-50 dark:bg-[#181818] rounded-full text-sm font-medium text-gray-600 dark:text-[#e5e5e5] mb-8 border border-gray-200/60 dark:border-[#2d2d2d]">
              <span className="text-base font-black text-gray-900 dark:text-white">C</span>
              <span>Welcome to ClubFinder Social</span>
            </div>

            <div className="flex justify-center w-full">
            <h1 className="font-black text-gray-900 dark:text-[#e5e5e5] mb-7 tracking-tight leading-[1.05] whitespace-nowrap text-[40px] sm:text-[46px] md:text-[58px] lg:text-[70px] xl:text-[82px] translate-x-[7px]">
              Discover Your Personal Community
            </h1>
          </div>
            <p
              ref={reveal}
              className="scroll-reveal reveal-delay-2 text-lg sm:text-xl text-gray-500 dark:text-gray-400 leading-relaxed font-normal max-w-2xl mx-auto mb-0"
              style={{ marginBottom: '12px' }}
            >
              Discover student clubs near you that match your interests
            </p>
            {user?.school ? (
              <div
                ref={reveal}
                className="scroll-reveal reveal-delay-2 mt-3 mb-10 inline-flex items-center gap-2.5 px-5 py-2 rounded-full text-sm font-semibold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-white/15 dark:to-white/5 text-white dark:text-gray-200 shadow-[0_0_20px_rgba(0,0,0,0.08)] backdrop-blur-xl border border-white/10 dark:border-white/10"
              >
                <span className="flex items-center justify-center w-5 h-5 rounded-full bg-white/20">
                  <GraduationCap className="h-3 w-3" />
                </span>
                <span className="text-white/80 dark:text-gray-400 text-[13px] font-medium">Your school</span>
                <span className="w-px h-4 bg-white/40 dark:bg-gray-500 rounded-full" aria-hidden="true" />
                <span className="font-semibold">{user.school}</span>
              </div>
            ) : (
              <Link
                to="/profile"
                ref={reveal}
                className="scroll-reveal reveal-delay-2 mt-3 mb-10 inline-flex items-center gap-2.5 px-5 py-2 rounded-full text-sm font-semibold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-white/15 dark:to-white/5 text-white dark:text-gray-200 shadow-[0_0_20px_rgba(0,0,0,0.08)] backdrop-blur-xl border border-white/10 dark:border-white/10 hover:opacity-90 transition-opacity"
              >
                <span className="flex items-center justify-center w-5 h-5 rounded-full bg-white/20">
                  <GraduationCap className="h-3 w-3" />
                </span>
                <span className="text-white/80 dark:text-gray-400 text-[13px] font-medium">Your school</span>
                <span className="w-px h-4 bg-white/40 dark:bg-gray-500 rounded-full" aria-hidden="true" />
                <span className="font-semibold">Set your university in Profile</span>
              </Link>
            )}
            
            <form
              ref={reveal}
              onSubmit={handleSearch}
              className="scroll-reveal reveal-delay-3 max-w-2xl mx-auto mb-0 relative overflow-visible z-30"
            >
              <div className="flex gap-2 items-center">
                <div className="flex gap-2 items-center flex-1 bg-white dark:bg-[#181818] rounded-full p-1.5 shadow-lg border border-gray-200 dark:border-[#2d2d2d]">
                  <div className="flex-1 relative">
                    <Search className="absolute left-5 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 h-5 w-5" />
                    <input
                      ref={searchInputRef}
                      type="text"
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setShowSearchResults(true);
                      }}
                      onFocus={() => {
                        if (searchResults.length > 0) setShowSearchResults(true);
                      }}
                      placeholder="Search for groups, interests, or activities..."
                      className="w-full pl-12 pr-4 py-4 rounded-full text-[15px] bg-transparent border-0 focus:outline-none focus:ring-0 text-gray-900 dark:text-[#e5e5e5] placeholder-gray-400 dark:placeholder-gray-500"
                    />
                    {isSearching && (
                      <Loader className="absolute right-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 animate-spin" />
                    )}
                  </div>
                  <button
                    type="submit"
                    className="flex items-center gap-2 px-7 py-3.5 bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-[15px] font-semibold rounded-full hover:bg-gray-800 dark:hover:bg-gray-100 transition-all duration-300"
                  >
                    <StickmanRunning className="h-5 w-5 flex-shrink-0" />
                    Search
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => setShowMap(true)}
                  className="flex items-center justify-center hover:opacity-80 transition-opacity bg-transparent rounded-xl p-0.5 w-14 h-14"
                  title="View Map"
                >
                  <span 
                    className="inline-block dark:hidden"
                    style={{ fontSize: '21.5px', filter: 'invert(1) grayscale(100%) contrast(1.2) brightness(0.95) saturate(0)' }}
                  >
                    🌎
                  </span>
                  <span 
                    className="hidden dark:inline-block"
                    style={{ fontSize: '21.5px', filter: 'grayscale(100%) invert(1) contrast(1.2) brightness(0.95) saturate(0)' }}
                  >
                    🌎
                  </span>
                </button>
              </div>

              {/* Search Results Dropdown */}
              {showSearchResults && searchQuery.trim() && (
                <div 
                  ref={searchResultsRef}
                  className="absolute top-full left-0 right-0 mt-3 bg-white dark:bg-[#181818] rounded-2xl shadow-2xl border border-gray-200/80 dark:border-[#2d2d2d] max-h-96 overflow-y-auto z-[9999]"
                >
                  {isSearching ? (
                    <div className="p-8 text-center">
                      <Loader className="h-6 w-6 animate-spin text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-500 dark:text-gray-400">Searching...</p>
                    </div>
                  ) : searchResults.length > 0 ? (
                    <div className="p-2">
                      {(() => {
                        // Organize results by category and hobby
                        const organizedResults = {};
                        const directGroupResults = [];
                        
                        searchResults.forEach((group, index) => {
                          const category = group.category || 'Other';
                          const hobbies = group.hobbies || group.tags || [];
                          const primaryHobby = hobbies[0] || 'General';
                          
                          // If group name matches search directly, show it first
                          if (group.name && group.name.toLowerCase().includes(searchQuery.toLowerCase())) {
                            directGroupResults.push({ group, index });
                          } else {
                            // Organize by category > hobby
                            if (!organizedResults[category]) {
                              organizedResults[category] = {};
                            }
                            if (!organizedResults[category][primaryHobby]) {
                              organizedResults[category][primaryHobby] = [];
                            }
                            organizedResults[category][primaryHobby].push({ group, index });
                          }
                        });
                        
                        let globalIndex = 0;
                        const allResults = [];
                        
                        // First, add direct group name matches
                        directGroupResults.forEach(({ group, index: originalIndex }) => {
                          allResults.push({ type: 'group', group, index: globalIndex++, originalIndex });
                        });
                        
                        // Then, add organized results by category > hobby
                        Object.keys(organizedResults).sort().forEach(category => {
                          Object.keys(organizedResults[category]).sort().forEach(hobby => {
                            organizedResults[category][hobby].forEach(({ group, index: originalIndex }) => {
                              allResults.push({ 
                                type: 'hierarchical', 
                                category, 
                                hobby, 
                                group, 
                                index: globalIndex++,
                                originalIndex 
                              });
                            });
                          });
                        });
                        
                        return allResults.map((item) => {
                          const location = item.group.location || {};
                          const locationStr = [
                            location.city,
                            location.state,
                            location.area || location.street
                          ].filter(Boolean).join(', ');
                          
                          const isSelected = selectedResultIndex === item.originalIndex;
                          
                          return (
                            <div
                              key={`${item.group.id}-${item.index}`}
                              onClick={() => handleResultClick(item.group.id)}
                              className={`px-4 py-3 rounded-lg cursor-pointer transition-colors ${
                                isSelected
                                  ? 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800'
                                  : 'hover:bg-gray-50 dark:hover:bg-gray-800'
                              }`}
                              onMouseEnter={() => setSelectedResultIndex(item.originalIndex)}
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1 min-w-0">
                                  {item.type === 'group' ? (
                                    // Direct group name match - simple display
                                    <>
                                      <div className="flex items-center gap-2 mb-1">
                                        <span className="text-gray-400 dark:text-gray-500 text-sm">#</span>
                                        <h3 className="font-semibold text-gray-900 dark:text-white truncate">
                                          {item.group.name}
                                        </h3>
                                      </div>
                                      {locationStr && (
                                        <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                                          {locationStr}
                                        </p>
                                      )}
                                    </>
                                  ) : (
                                    // Hierarchical display: Category > Hobby > Group
                                    <>
                                      <div className="flex items-center gap-1 mb-1 text-sm">
                                        <span className="text-gray-400 dark:text-gray-500">#</span>
                                        <span className="text-gray-600 dark:text-gray-400">{item.category}</span>
                                        <span className="text-gray-400 dark:text-gray-500"> &gt; </span>
                                        <span className="text-gray-600 dark:text-gray-400 font-medium">{item.hobby}</span>
                                        <span className="text-gray-400 dark:text-gray-500"> &gt; </span>
                                        <span className="text-gray-900 dark:text-white font-semibold">{item.group.name}</span>
                                      </div>
                                      {locationStr && (
                                        <p className="text-xs text-gray-500 dark:text-gray-500 truncate mt-0.5">
                                          {locationStr}
                                        </p>
                                      )}
                                    </>
                                  )}
                                </div>
                                {isSelected && (
                                  <div className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                                    ↵
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  ) : (
                    <div className="p-8 text-center">
                      <p className="text-sm text-gray-500 dark:text-gray-400">No groups found for "{searchQuery}"</p>
                    </div>
                  )}
                  {searchResults.length > 0 && (
                    <div className="px-4 py-2 border-t border-gray-200 dark:border-[#2d2d2d] bg-gray-50 dark:bg-[#141414]">
                      <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                        <span>↑ ↓ to navigate</span>
                        <span>↵ to select</span>
                        <span>esc to close</span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </form>

            {/* Animated Interest Pills */}
            <div className="mt-10 overflow-hidden max-w-3xl mx-auto relative z-0">
              <div className="absolute left-0 top-0 bottom-0 w-12 bg-gradient-to-r from-white dark:from-[#0f0f0f] to-transparent z-10 pointer-events-none" />
              <div className="absolute right-0 top-0 bottom-0 w-12 bg-gradient-to-l from-white dark:from-[#0f0f0f] to-transparent z-10 pointer-events-none" />
              <div className="flex animate-marquee w-max gap-3">
                {[
                  'Hiking', 'Reading', 'Sports', 'Music', 'Photography', 'Gaming',
                  'Cooking', 'Fitness', 'Art', 'Dance', 'Tech', 'Volunteering',
                  'Film', 'Writing', 'Yoga', 'Tennis', 'Running', 'Travel',
                  'Hiking', 'Reading', 'Sports', 'Music', 'Photography', 'Gaming',
                  'Cooking', 'Fitness', 'Art', 'Dance', 'Tech', 'Volunteering',
                  'Film', 'Writing', 'Yoga', 'Tennis', 'Running', 'Travel',
                ].map((interest, i) => (
                  <button
                    key={`${interest}-${i}`}
                    onClick={() => {
                      setSearchQuery(interest);
                      setShowSearchResults(true);
                    }}
                    className="flex-shrink-0 px-4 py-2 rounded-full text-[14px] font-medium text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-[#181818] border border-gray-200/60 dark:border-[#2d2d2d] hover:border-gray-400 dark:hover:border-gray-500 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-[#252525] transition-all duration-300 cursor-pointer hover:shadow-sm"
                  >
                    {interest}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
          
      {/* AI Recommendations */}
      <div className="w-full bg-white dark:bg-[#0f0f0f]" data-tour="home-recommendations">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-20 w-full">
                <div ref={reveal} className="scroll-reveal text-center mb-12">
                  <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-[#e5e5e5] tracking-tight leading-snug">Enter your hobbies and interests<br className="hidden sm:block" /> for personalized student groups</h2>
                  <p className="text-base text-gray-500 dark:text-gray-400 mt-3 max-w-xl mx-auto">Let us recommend the best clubs and communities for you</p>
                </div>
              
              <div className="flex justify-center mb-10">
                <button
                  onClick={handleIntelligentFill}
                  disabled={isAgentThinking}
                  className="flex items-center gap-2.5 px-7 py-3 bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-sm font-semibold rounded-full hover:bg-gray-800 dark:hover:bg-gray-100 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg"
                  title="Auto-fill location, hobbies, and tags"
                >
                  {isAgentThinking ? (
                    <>
                      <Loader className="h-5 w-5 animate-spin" />
                      <span>AI Thinking...</span>
                    </>
                  ) : (
                    <>
                      <GeminiLogo className="h-6 w-6 flex-shrink-0" size={24} />
                      <span>Autofill</span>
                    </>
                  )}
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-12 gap-5 mb-8 max-w-5xl mx-auto">
                <div ref={reveal} className="scroll-reveal reveal-delay-1 md:col-span-4 bg-white dark:bg-[#181818] rounded-2xl p-6 border border-gray-200/70 dark:border-[#2d2d2d] shadow-sm min-w-0 hover:shadow-md hover:border-gray-300 dark:hover:border-[#3d3d3d] transition-all duration-300">
                  <div className="relative mb-4">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-[#e5e5e5] flex items-center gap-2.5">
                      <span className="w-2 h-2 bg-gray-900 dark:bg-white rounded-full"></span>
                      Location
                      <button
                        type="button"
                        onClick={() => setShowCountryPicker(!showCountryPicker)}
                        className="flex items-center gap-1 px-1.5 py-0.5 rounded-md hover:bg-gray-100 dark:hover:bg-[#2a2a2a] transition-colors"
                        title="Change country"
                      >
                        <img src={`https://flagcdn.com/w40/${(country || 'us').toLowerCase()}.png`} alt="" className="w-6 h-4 object-cover rounded-[2px]" />
                      </button>
                    </h3>
                    {showCountryPicker && (
                      <div className="absolute right-0 top-8 z-50 w-52 max-h-60 overflow-y-auto rounded-xl border border-gray-200 dark:border-[#3d3d3d] bg-white dark:bg-[#1f1f1f] shadow-xl">
                        {countries.map(c => (
                          <button
                            key={c.code}
                            type="button"
                            onClick={() => {
                              setCountry(c.code);
                              setState('');
                              setCity('');
                              setArea('');
                              setPincode('');
                              setShowCountryPicker(false);
                            }}
                            className={`w-full flex items-center gap-2.5 px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-[#2a2a2a] transition-colors ${country === c.code ? 'bg-blue-50 dark:bg-blue-900/20 font-semibold' : ''}`}
                          >
                            <img src={`https://flagcdn.com/w40/${c.code.toLowerCase()}.png`} alt="" className="w-6 h-4 object-cover rounded-[2px]" />
                            <span className="text-gray-900 dark:text-white">{c.name}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={handleUseMyLocation}
                    disabled={isDetectingLocation}
                    className="w-full mb-3 px-4 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 text-white text-sm font-semibold rounded-xl hover:from-blue-700 hover:to-cyan-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg hover:shadow-xl"
                  >
                    <MapPin className="h-3.5 w-3.5" />
                    {isDetectingLocation ? 'Detecting...' : 'Use My Location'}
                  </button>
                  {locationError && (
                    <div className="mb-3 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                      <p className="text-[11px] text-red-600 dark:text-red-300">{locationError}</p>
                    </div>
                  )}
                  <div className="relative w-full mb-4" style={{ minHeight: 160 }}>
                    <div
                      ref={locationMapRef}
                      className="location-map-inline w-full h-40 rounded-xl overflow-hidden border border-gray-200 dark:border-[#3d3d3d] bg-gray-100 dark:bg-[#252525]"
                      style={{ minHeight: 160 }}
                    />
                    <div
                      ref={locationPinOverlayRef}
                      className="absolute inset-0 rounded-xl pointer-events-none"
                      style={{ minHeight: 160 }}
                      aria-hidden
                    />
                  </div>
                  <div className="rounded-xl border border-gray-200 dark:border-[#3d3d3d] bg-gray-50 dark:bg-[#252525] px-4 py-3 space-y-2">
                    <div className="flex justify-between items-center text-[13px]">
                      <span className="font-semibold text-gray-900 dark:text-[#e5e5e5]">Country</span>
                      <span className="text-gray-600 dark:text-gray-400">
                        {(() => {
                          const info = countries.find(c => c.code === (country || 'US'));
                          return info ? info.name : 'Select Country';
                        })()}
                      </span>
                    </div>
                    <div className="flex justify-between items-center text-[13px]">
                      <span className="font-semibold text-gray-900 dark:text-[#e5e5e5]">State <span className="text-red-500">*</span></span>
                      <span className="text-gray-600 dark:text-gray-400">{state ? (usStates.find(s => s.code === state)?.name || state) : 'Select State'}</span>
                    </div>
                    <div className="flex justify-between items-center text-[13px]">
                      <span className="font-semibold text-gray-900 dark:text-[#e5e5e5]">City <span className="text-red-500">*</span></span>
                      <span className="text-gray-600 dark:text-gray-400">{city || '—'}</span>
                    </div>
                    <div className="flex justify-between items-center text-[13px]">
                      <span className="font-semibold text-gray-900 dark:text-[#e5e5e5]">Area (Optional)</span>
                      <span className="text-gray-600 dark:text-gray-400">{area || '—'}</span>
                    </div>
                    <div className="flex justify-between items-center text-[13px]">
                      <span className="font-semibold text-gray-900 dark:text-[#e5e5e5]">Zip Code (Optional)</span>
                      <span className="text-gray-600 dark:text-gray-400">{pincode || '—'}</span>
                    </div>
                  </div>
                  {state && city && (
                    <p className="mt-2 text-[11px] text-blue-600 dark:text-blue-400">
                      {isLoadingClubsCount ? 'Finding clubs...' : clubsNearYouCount != null ? `Found ${clubsNearYouCount} club${clubsNearYouCount !== 1 ? 's' : ''} near you` : null}
                    </p>
                  )}
                </div>
                
                <div ref={reveal} className="scroll-reveal reveal-delay-2 md:col-span-4 bg-white dark:bg-[#181818] rounded-2xl p-6 border border-gray-200/70 dark:border-[#2d2d2d] shadow-sm min-w-0 hover:shadow-md hover:border-gray-300 dark:hover:border-[#3d3d3d] transition-all duration-300">
                  <HobbiesDropdown
                    selectedHobbies={selectedHobbies}
                    onHobbiesChange={setSelectedHobbies}
                    label="Hobbies & Interests (Optional)"
                    required={false}
                  />
                  <div className="mt-4">
                    <p className="text-[13px] font-semibold text-gray-900 dark:text-[#e5e5e5] mb-2.5">
                      Member Size
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {[
                        { value: '', label: 'Any size', icon: 'any' },
                        { value: 'small', label: 'Under 20', icon: 'one' },
                        { value: 'medium', label: '20–50', icon: 'group' },
                        { value: 'large', label: '50+', icon: 'crowd' }
                      ].map(({ value, label, icon }) => (
                        <button
                          key={value || 'any'}
                          type="button"
                          onClick={() => setClubSize(value)}
                          className={`flex flex-col items-center justify-center gap-2 py-3 px-3 rounded-2xl border text-[13px] font-medium transition-all duration-200 ${
                            clubSize === value
                              ? 'border-blue-500/80 bg-blue-500/10 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400 shadow-sm'
                              : 'border-gray-200/80 dark:border-[#333] bg-white dark:bg-[#222] text-gray-500 dark:text-gray-400 hover:border-gray-300 dark:hover:border-[#444] hover:bg-gray-50 dark:hover:bg-[#282828]'
                          }`}
                        >
                          <span className={`flex items-center justify-center gap-0.5 h-7 ${clubSize !== value ? 'text-gray-400 dark:text-inherit' : ''}`}>
                            {icon === 'any' && <SlidersHorizontal className="h-5 w-5 opacity-80" />}
                            {icon === 'one' && <User className="h-5 w-5" />}
                            {icon === 'group' && <Users className="h-5 w-5" />}
                            {icon === 'crowd' && (
                              <span className="inline-grid grid-cols-2 gap-0.5">
                                <User className="h-3 w-3" />
                                <User className="h-3 w-3" />
                                <User className="h-3 w-3" />
                                <User className="h-3 w-3" />
                              </span>
                            )}
                          </span>
                          <span className="leading-tight whitespace-nowrap">{label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="mt-4">
                    <p className="text-[13px] font-semibold text-gray-900 dark:text-[#e5e5e5] mb-2.5">
                      Club Type
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {[
                        { value: '', label: 'Any type', Icon: Globe },
                        { value: 'open', label: 'Open', Icon: DoorOpen },
                        { value: 'private', label: 'Private', Icon: Lock },
                        { value: 'vip', label: 'Exclusive', Icon: Crown }
                      ].map(({ value, label, Icon }) => (
                        <button
                          key={value || 'any'}
                          type="button"
                          onClick={() => setClubType(value)}
                          className={`flex flex-col items-center justify-center gap-2 py-3 px-3 rounded-2xl border text-[13px] font-medium transition-all duration-200 ${
                            clubType === value
                              ? 'border-blue-500/80 bg-blue-500/10 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400 shadow-sm'
                              : 'border-gray-200/80 dark:border-[#333] bg-white dark:bg-[#222] text-gray-500 dark:text-gray-400 hover:border-gray-300 dark:hover:border-[#444] hover:bg-gray-50 dark:hover:bg-[#282828]'
                          }`}
                        >
                          <span className={`flex items-center justify-center h-7 w-full ${clubType !== value ? 'text-gray-400 dark:text-gray-400' : ''}`}>
                            <Icon
                              className={`h-5 w-5 shrink-0 ${value === 'vip' ? 'text-amber-500 dark:text-amber-400' : ''}`}
                              fill={value === 'vip' ? 'currentColor' : 'none'}
                              strokeWidth={1.5}
                            />
                          </span>
                          <span className="leading-tight">{label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div ref={reveal} className="scroll-reveal reveal-delay-3 md:col-span-4 bg-white dark:bg-[#181818] rounded-2xl p-6 border border-gray-200/70 dark:border-[#2d2d2d] shadow-sm min-w-0 hover:shadow-md hover:border-gray-300 dark:hover:border-[#3d3d3d] transition-all duration-300">
                  <SmartTagsInput
                    tags={tags}
                    onTagsChange={setTags}
                    selectedHobbies={selectedHobbies}
                    locationContext={{ state, city, area, pincode }}
                    label="Additional Tags"
                    required={false}
                  />

                  {/* Timings selection (custom dropdown with outline SVGs) */}
                  <div className="mt-6">
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">
                      Timings
                    </label>
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setIsTimingOpen((v) => !v)}
                        className="w-full px-3 py-2.5 rounded-xl text-[13px] border border-gray-200/80 dark:border-[#333] bg-white dark:bg-[#222] text-gray-900 dark:text-[#e5e5e5] focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-100 focus:border-transparent transition-all flex items-center justify-between"
                      >
                        <span className="flex items-center gap-2">
                          {timing === 'daytime' && (
                            <svg className="h-4 w-4 text-yellow-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <circle cx="12" cy="12" r="4" />
                              <path d="M12 3v2M12 19v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M3 12h2M19 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
                            </svg>
                          )}
                          {timing === 'noon' && (
                            <svg className="h-4 w-4 text-amber-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <circle cx="12" cy="10" r="4" />
                              <path d="M4 16h16" />
                            </svg>
                          )}
                          {timing === 'evening' && (
                            <svg className="h-4 w-4 text-orange-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M4 16h16" />
                              <path d="M6 16a6 6 0 1 1 12 0" />
                            </svg>
                          )}
                          {timing === 'night' && (
                            <svg className="h-4 w-4 text-indigo-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M20 12.5A7.5 7.5 0 0 1 11.5 4 6 6 0 1 0 20 12.5Z" />
                              <path d="M4 4h.01M7 2h.01M22 17h.01M19 21h.01" />
                            </svg>
                          )}
                          {timing === '' && (
                            <svg className="h-4 w-4 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                              <circle cx="12" cy="13" r="7" />
                              <path d="M12 10v3.5l2.5 2.5" />
                              <path d="M8 7v-2M16 7v-2" />
                              <circle cx="8" cy="4" r="1.5" />
                              <circle cx="16" cy="4" r="1.5" />
                              <path d="M8 20h2M14 20h2" />
                            </svg>
                          )}
                          <span className={timing === '' ? 'text-gray-500 dark:text-gray-400' : ''}>
                            {timing === 'daytime'
                              ? 'Daytime'
                              : timing === 'noon'
                              ? 'Noon'
                              : timing === 'evening'
                              ? 'Evening'
                              : timing === 'night'
                              ? 'Night'
                              : 'Enter availability'}
                          </span>
                        </span>
                        <span className="text-gray-400">▾</span>
                      </button>

                      {isTimingOpen && (
                        <div className="absolute z-20 mt-1 w-full rounded-xl border border-gray-200/80 dark:border-[#333] bg-white dark:bg-[#222] shadow-lg overflow-hidden text-[13px]">
                          {[
                            { value: '', label: 'Enter availability' },
                            { value: 'daytime', label: 'Daytime' },
                            { value: 'noon', label: 'Noon' },
                            { value: 'evening', label: 'Evening' },
                            { value: 'night', label: 'Night' }
                          ].map(({ value, label }) => (
                            <button
                              key={value || 'any'}
                              type="button"
                              onClick={() => {
                                setTiming(value);
                                setIsTimingOpen(false);
                              }}
                              className={`w-full px-3 py-2 flex items-center gap-2 hover:bg-gray-50 dark:hover:bg-[#2a2a2a] ${
                                timing === value ? 'bg-gray-50 dark:bg-[#262626] font-medium' : ''
                              }`}
                            >
                              {value === 'daytime' && (
                                <svg className="h-4 w-4 text-yellow-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <circle cx="12" cy="12" r="4" />
                                  <path d="M12 3v2M12 19v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M3 12h2M19 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
                                </svg>
                              )}
                              {value === 'noon' && (
                                <svg className="h-4 w-4 text-amber-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <circle cx="12" cy="10" r="4" />
                                  <path d="M4 16h16" />
                                </svg>
                              )}
                              {value === 'evening' && (
                                <svg className="h-4 w-4 text-orange-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M4 16h16" />
                                  <path d="M6 16a6 6 0 1 1 12 0" />
                                </svg>
                              )}
                              {value === 'night' && (
                                <svg className="h-4 w-4 text-indigo-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M20 12.5A7.5 7.5 0 0 1 11.5 4 6 6 0 1 0 20 12.5Z" />
                                  <path d="M4 4h.01M7 2h.01M22 17h.01M19 21h.01" />
                                </svg>
                              )}
                              {value === '' && (
                                <svg className="h-4 w-4 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                                  <circle cx="12" cy="13" r="7" />
                                  <path d="M12 10v3.5l2.5 2.5" />
                                  <path d="M8 7v-2M16 7v-2" />
                                  <circle cx="8" cy="4" r="1.5" />
                                  <circle cx="16" cy="4" r="1.5" />
                                  <path d="M8 20h2M14 20h2" />
                                </svg>
                              )}
                              <span>{label}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              
              <button
                onClick={handleGetRecommendations}
                disabled={isLoading || (!selectedSchool && (!state || !city))}
                className="w-full max-w-md mx-auto block px-6 py-4 mb-8 bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-base font-semibold rounded-full hover:bg-gray-800 dark:hover:bg-gray-100 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg"
              >
                {isLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader className="h-5 w-5 animate-spin" />
                    Loading...
                  </span>
                ) : (
                  'Get Recommendations'
                )}
              </button>

              <div className="pt-16 pb-8">
                <p className="text-center text-xs font-medium text-gray-400 dark:text-gray-500 mb-8 uppercase tracking-widest">
                  Built by students for students
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4 md:gap-6">
                  <SchoolLogo name="University of Washington" localLogo="/logos/Washington_Huskies_logo.png" logoUrl="" fallbackText="UW" color="purple-700" websiteUrl="https://www.washington.edu" />
                  <SchoolLogo name="Seattle University" localLogo="/logos/117-1173046_seattle-university-athletics-logo.png" logoUrl="" fallbackText="SU" color="red-700" websiteUrl="https://www.seattleu.edu" />
                  <SchoolLogo name="Seattle Pacific University" localLogo="/logos/logo-details-main.png" logoUrl="" fallbackText="SPU" color="gray-800" websiteUrl="https://spu.edu" />
                  <SchoolLogo name="UW Bothell" localLogo="/logos/Stacked-W-Updated2025.png" logoUrl="" fallbackText="UW Bothell" color="purple-700" websiteUrl="https://www.uwb.edu" />
                  <SchoolLogo name="Bellevue College" localLogo="/logos/Bellevue_College_logo.png" logoUrl="" fallbackText="Bellevue College" color="blue-700" websiteUrl="https://www.bellevuecollege.edu" />
                  <SchoolLogo name="Shoreline Community College" localLogo="/logos/pennants32.png" logoUrl="" fallbackText="Shoreline CC" color="green-700" websiteUrl="https://www.shoreline.edu" />
                  <SchoolLogo name="North Seattle College" localLogo="/logos/NSC.png" logoUrl="" fallbackText="North Seattle" color="blue-600" websiteUrl="https://northseattle.edu" />
                  <SchoolLogo name="South Seattle College" localLogo="/logos/SouthSeattle.png" logoUrl="" fallbackText="South Seattle" color="orange-600" websiteUrl="https://southseattle.edu" />
                </div>
              </div>

              <div className="pt-20">
        <div ref={reveal} className="scroll-reveal text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-[#e5e5e5] mb-5 tracking-tight">Why ClubFinder Social?</h2>
          <p className="text-lg text-gray-500 dark:text-gray-400 max-w-2xl mx-auto whitespace-nowrap">Making it easy for you to build communities and share hobbies in a new place</p>
        </div>

        <div className="mb-20 flex justify-center px-4">
          <div className="relative w-full max-w-6xl">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 md:gap-6">
              <div className="relative overflow-hidden rounded-3xl shadow-sm aspect-[4/3] min-h-[240px] md:min-h-[320px]">
                <img src="/hiking.jpg" alt="Student hiking group" className="w-full h-full object-cover" />
              </div>
              <div className="relative overflow-hidden rounded-3xl shadow-sm aspect-[4/3] min-h-[240px] md:min-h-[320px]">
                <img src="/reading.jpg" alt="Student reading group" className="w-full h-full object-cover" />
              </div>
              <div className="relative overflow-hidden rounded-3xl shadow-sm aspect-[4/3] min-h-[240px] md:min-h-[320px]">
                <img src="/tennis.jpg" alt="Student tennis group" className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          <Link ref={reveal} to="/groups" className="scroll-reveal reveal-delay-1 group block text-center p-10 rounded-3xl bg-gray-50 dark:bg-[#181818] border border-gray-100 dark:border-[#2d2d2d] hover:shadow-lg transition-all duration-300 hover:border-gray-200 dark:hover:border-[#3d3d3d] hover:-translate-y-1">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white dark:bg-[#1f1f1f] rounded-2xl mb-6 shadow-sm group-hover:shadow-md transition-shadow duration-300">
              <span className="text-2xl font-bold text-gray-900 dark:text-[#e5e5e5]" style={{ fontSize: '33px' }} aria-hidden>
                C
              </span>
            </div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-[#e5e5e5] mb-3">AI-Powered Matching</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
              Our intelligent system learns your interests and recommends groups that truly match your passions.
            </p>
          </Link>

          <Link ref={reveal} to="/reels" className="scroll-reveal reveal-delay-2 group block text-center p-10 rounded-3xl bg-gray-50 dark:bg-[#181818] border border-gray-100 dark:border-[#2d2d2d] hover:shadow-lg transition-all duration-300 hover:border-gray-200 dark:hover:border-[#3d3d3d] hover:-translate-y-1">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white dark:bg-[#1f1f1f] rounded-2xl mb-6 shadow-sm group-hover:shadow-md transition-shadow duration-300">
              <Search className="h-8 w-8 text-gray-900 dark:text-[#e5e5e5]" style={{ height: '33px', width: '33px' }} />
            </div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-[#e5e5e5] mb-3">Easy Discovery</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
              Search by category, interest, or keyword. Find exactly what you're looking for in seconds.
            </p>
          </Link>

          <Link ref={reveal} to="/friends" className="scroll-reveal reveal-delay-3 group block text-center p-10 rounded-3xl bg-gray-50 dark:bg-[#181818] border border-gray-100 dark:border-[#2d2d2d] hover:shadow-lg transition-all duration-300 hover:border-gray-200 dark:hover:border-[#3d3d3d] hover:-translate-y-1">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white dark:bg-[#1f1f1f] rounded-2xl mb-6 shadow-sm group-hover:shadow-md transition-shadow duration-300">
              <Users className="h-8 w-8 text-gray-900 dark:text-[#e5e5e5]" style={{ height: '33px', width: '33px' }} />
            </div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-[#e5e5e5] mb-3">Connect & Grow</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
              Join exclusive communities that help you grow, learn, and make lasting friendships.
            </p>
          </Link>
        </div>
              </div>

              <div ref={reveal} className="scroll-reveal pt-24 pb-12 text-center">
                <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-[#e5e5e5] mb-5 tracking-tight">Ready to Find Your<br className="hidden sm:block" /> Personal Community?</h2>
                <p className="text-lg text-gray-500 dark:text-gray-400">Trusted By Students From Top Universities</p>
              </div>
        </div>
      </div>

      {/* Map Modal */}
      <WorldMapCopy 
        isOpen={showMap} 
        onClose={() => setShowMap(false)}
        mapboxToken={mapboxToken}
        user={user}
        showMemoryPins={false}
        allowPinsSwitch={true}
      />
    </div>
  );
};

export default Home;
